import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import { Avatar, Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import brandLockup from '../../../asset/logo/lockup-horizontal-color.svg';
import { BLUE, BLUE_TEXT, BORDER, GREEN, INTER, NAVY, POPPINS, primaryButtonSx, SLATE, SOFT_BLUE, SUCCESS_SOFT, SUCCESS_TEXT } from '../teacherStyles';

export type TeacherStage = 'description' | 'plan' | 'exercises';

interface TeacherHeaderProps {
    stage: TeacherStage;
    onStartOver: () => void;
    onNavigate: (stage: TeacherStage) => void;
    onPreview?: () => void;
    onPublish?: () => void;
    onAudio?: () => void;
}

const steps: { id: TeacherStage; label: string }[] = [
    { id: 'description', label: 'Description' },
    { id: 'plan', label: 'Lesson plan' },
    { id: 'exercises', label: 'Exercises' },
];

export default function TeacherHeader({ stage, onStartOver, onNavigate, onPreview, onPublish, onAudio }: TeacherHeaderProps) {
    const history = useHistory();
    const activeIndex = steps.findIndex(step => step.id === stage);

    return (
        <Box component="header" sx={{ height: 72, px: { xs: 2, sm: 3, lg: 4 }, backgroundColor: '#fff', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <Button onClick={() => history.push('/mockup/teacher')} sx={{ p: 0, minWidth: 0, color: NAVY }}>
                <Box component="img" src={brandLockup} alt="VerbaSchola" sx={{ height: { xs: 22, sm: 25 }, width: 'auto', display: 'block' }} />
            </Button>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1, ml: 4 }}>
                {steps.map((step, index) => (
                    <Stack key={step.id} direction="row" alignItems="center" spacing={1}>
                        {index > 0 && <Box sx={{ width: 24, height: 1, backgroundColor: index <= activeIndex ? BLUE : BORDER }} />}
                        <Button
                            onClick={() => onNavigate(step.id)}
                            aria-current={index === activeIndex ? 'step' : undefined}
                            sx={{ minWidth: 0, p: 0.5, borderRadius: '10px', textTransform: 'none' }}
                        >
                            <Box sx={{ width: 24, height: 24, mr: 1, borderRadius: '50%', display: 'grid', placeItems: 'center', backgroundColor: index <= activeIndex ? BLUE : '#fff', border: `1px solid ${index <= activeIndex ? BLUE : BORDER}` }}>
                                {index < activeIndex
                                    ? <CheckCircleRoundedIcon sx={{ color: '#fff', fontSize: 17 }} />
                                    : <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11, color: index <= activeIndex ? '#fff' : SLATE }}>{index + 1}</Typography>}
                            </Box>
                            <Typography sx={{ fontFamily: INTER, fontWeight: index === activeIndex ? 700 : 500, fontSize: 13, color: index === activeIndex ? NAVY : SLATE }}>
                                {step.label}
                            </Typography>
                        </Button>
                    </Stack>
                ))}
            </Box>

            <Chip
                label={`${activeIndex + 1}/3 · ${steps[activeIndex].label}`}
                size="small"
                sx={{ display: { xs: 'flex', md: 'none' }, ml: 1.5, fontFamily: INTER, fontWeight: 700, color: BLUE_TEXT, backgroundColor: SOFT_BLUE }}
            />

            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 'auto' }}>
                <Chip icon={<CheckCircleRoundedIcon />} label="Auto-saved" size="small" sx={{ display: { xs: 'none', lg: 'flex' }, fontFamily: INTER, color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT, '& .MuiChip-icon': { color: GREEN } }} />
                {stage !== 'description' && (
                    <Button onClick={onStartOver} sx={{ display: { xs: 'none', lg: 'flex' }, fontFamily: INTER, color: SLATE, px: 1.5, py: 1 }}>
                        Start over
                    </Button>
                )}
                {stage === 'plan' && (
                    <Button startIcon={<PrintRoundedIcon />} onClick={onPreview} sx={{ display: { xs: 'none', sm: 'inline-flex' }, fontFamily: INTER, color: BLUE, px: 1.5, py: 1 }}>
                        Print preview
                    </Button>
                )}
                {stage === 'exercises' && (
                    <>
                        <IconButton aria-label="Manage lesson audio" onClick={onAudio} sx={{ display: { xs: 'inline-flex', md: 'none' }, color: BLUE }}>
                            <GraphicEqRoundedIcon />
                        </IconButton>
                        <Button startIcon={<GraphicEqRoundedIcon />} onClick={onAudio} sx={{ display: { xs: 'none', md: 'inline-flex' }, fontFamily: INTER, color: BLUE, px: 1.5, py: 1 }}>
                            Audio
                        </Button>
                        <Button startIcon={<PrintRoundedIcon />} onClick={() => window.print()} sx={{ display: { xs: 'none', lg: 'flex' }, fontFamily: INTER, color: SLATE, px: 1.5, py: 1 }}>
                            Print
                        </Button>
                        <Button startIcon={<VisibilityRoundedIcon />} onClick={onPreview} sx={{ display: { xs: 'none', sm: 'inline-flex' }, fontFamily: INTER, color: BLUE, px: 1.5, py: 1 }}>
                            Preview
                        </Button>
                        <Button variant="contained" onClick={onPublish} sx={{ ...primaryButtonSx, px: 2.5, py: 1.25 }}>
                            Review & publish
                        </Button>
                    </>
                )}
                <Avatar sx={{ display: { xs: 'none', sm: 'flex' }, width: 36, height: 36, ml: 1, backgroundColor: NAVY, fontFamily: POPPINS, fontSize: 13 }}>
                    EF
                </Avatar>
            </Stack>
        </Box>
    );
}
